import { useId, useState } from "react";
import { Button, Input } from "@legal-500/legal500-design-system";
import { aiParser, type AiTestDocument } from "@/api/aiParser.service";
import { useQuery } from "@/hooks/useApi";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { formatDateShort } from "@/utils/dateFormatter";
import styles from "./AiSchemas.module.css";

const MIN_SEARCH_LENGTH = 2;

type SubmissionDocumentPickerProps = {
  selected: AiTestDocument | null;
  onSelect: (document: AiTestDocument | null) => void;
  disabled: boolean;
};

function DocumentDetails({ document }: { document: AiTestDocument }) {
  const details = [
    document.firmName ?? "Unknown firm",
    document.label,
    document.uploadedAt ? `Uploaded ${formatDateShort(document.uploadedAt)}` : null,
  ].filter((detail): detail is string => Boolean(detail));

  return (
    <>
      <span className={styles.documentName}>{document.fileName}</span>
      {document.sharepointFileName && document.sharepointFileName !== document.fileName && (
        <span className={styles.documentMeta}>SharePoint: {document.sharepointFileName}</span>
      )}
      <span className={styles.documentMeta}>{details.join(" · ")}</span>
    </>
  );
}

export function SubmissionDocumentPicker({
  selected,
  onSelect,
  disabled,
}: SubmissionDocumentPickerProps) {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const canSearch = debouncedSearch.length >= MIN_SEARCH_LENGTH;
  const resultsId = useId();

  const { data, loading, error } = useQuery(
    () =>
      canSearch
        ? aiParser.listTestDocuments(debouncedSearch)
        : Promise.resolve({ documents: [] as AiTestDocument[] }),
    [debouncedSearch, canSearch],
  );

  if (selected) {
    return (
      <div className={styles.selectedDocument} role="group" aria-label="Selected document">
        <div className={styles.documentText}>
          <DocumentDetails document={selected} />
        </div>
        <Button size="small" variant="outlined" disabled={disabled} onClick={() => onSelect(null)}>
          Change document
        </Button>
      </div>
    );
  }

  const documents = data?.documents ?? [];
  const settled = canSearch && !loading && search.trim() === debouncedSearch;

  return (
    <div className={styles.documentPicker}>
      <Input
        label="Search submission documents"
        type="search"
        value={search}
        onChange={event => setSearch(event.target.value)}
        placeholder="File name, SharePoint file name, firm or label"
        helperText={`Type at least ${MIN_SEARCH_LENGTH} characters.`}
        aria-controls={resultsId}
        disabled={disabled}
        fullWidth
      />

      {error && (
        <div className={styles.errorBanner} role="alert">
          {error.message}
        </div>
      )}

      {canSearch && loading && (
        <p className={styles.dropHint} role="status">
          Searching…
        </p>
      )}

      {settled && !error && documents.length === 0 && (
        <p className={styles.dropHint} role="status">
          No submission documents match “{debouncedSearch}”.
        </p>
      )}

      <ul id={resultsId} className={styles.documentResults} aria-label="Matching documents">
        {settled &&
          documents.map(document => (
            <li key={document.submissionEntryFileId}>
              <button
                type="button"
                className={styles.documentOption}
                disabled={disabled}
                onClick={() => onSelect(document)}
              >
                <DocumentDetails document={document} />
              </button>
            </li>
          ))}
      </ul>
    </div>
  );
}
